import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiClient } from "@/lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("inxpek_admin_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem("inxpek_admin_token"));
  const [loading, setLoading] = useState(false);

  const finishLogin = useCallback((data) => {
    localStorage.setItem("inxpek_admin_token", data.token);
    localStorage.setItem("inxpek_admin_user", JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
  }, []);

  // 1.º passo: email + palavra-passe. Se a verificação em 2 passos estiver
  // ligada, devolve { ok, requires2fa, challenge } SEM autenticar.
  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await apiClient.post("/auth/login", { email, password });
      if (data?.requires_2fa) {
        return { ok: true, requires2fa: true, challenge: data };
      }
      finishLogin(data);
      return { ok: true };
    } catch (e) {
      const msg = e?.response?.data?.detail || "Falha na autenticação";
      return { ok: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, [finishLogin]);

  // 2.º passo: código de 6 dígitos recebido por email
  const verify2fa = useCallback(async (challengeId, code) => {
    setLoading(true);
    try {
      const { data } = await apiClient.post("/auth/2fa/verify", { challenge_id: challengeId, code });
      finishLogin(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Código inválido", status: e?.response?.status };
    } finally {
      setLoading(false);
    }
  }, [finishLogin]);

  const logout = useCallback(() => {
    localStorage.removeItem("inxpek_admin_token");
    localStorage.removeItem("inxpek_admin_user");
    setToken(null);
    setUser(null);
  }, []);

  // Validar token na montagem inicial (se existe user mas em novo tab)
  useEffect(() => {
    if (token && !user) {
      apiClient
        .get("/auth/me")
        .then(({ data }) => {
          setUser(data);
          localStorage.setItem("inxpek_admin_user", JSON.stringify(data));
        })
        .catch(() => logout());
    }
  }, [token, user, logout]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, verify2fa, logout, replaceSession: finishLogin, isAuthed: !!token }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve estar dentro de AuthProvider");
  return ctx;
}
